import { useMemo } from "react";
import { cn } from "@/lib/utils";

interface SparklineProps {
  /** Values oldest → newest. */
  values: number[];
  /** CSS colour. Accepts CSS vars — e.g. "var(--color-brand-primary)". */
  stroke?: string;
  /** Optional area fill. Omit for line-only. */
  fill?: string;
  strokeWidth?: number;
  className?: string;
  /** SVG width in px. Defaults to responsive (fills parent). */
  width?: number;
  height?: number;
  /** Show a dot at the last point. */
  showEndDot?: boolean;
  ariaLabel?: string;
}

/**
 * Minimal trend-line SVG. No axes, no ticks, no labels.
 *
 * Designed for inline display inside counter tiles, table cells, or KPI cards.
 * Renders a smooth path; normalises input to its own min/max. For comparative
 * sparklines across tiles, pass a shared domain externally by pre-normalising.
 */
export function Sparkline({
  values,
  stroke = "var(--color-brand-primary)",
  fill,
  strokeWidth = 1.5,
  className,
  width = 120,
  height = 32,
  showEndDot = true,
  ariaLabel,
}: SparklineProps) {
  const { pathD, areaD, endX, endY } = useMemo(() => {
    if (values.length < 2) {
      return { pathD: "", areaD: "", endX: 0, endY: 0 };
    }
    const min = Math.min(...values);
    const max = Math.max(...values);
    const span = max - min || 1;
    const stepX = width / (values.length - 1);
    const pad = 2;
    const usableH = height - pad * 2;

    const points = values.map((v, i) => {
      const x = i * stepX;
      const y = pad + usableH - ((v - min) / span) * usableH;
      return [x, y] as const;
    });

    // Smooth monotone cubic — read well for trend data.
    let d = `M ${points[0]![0]} ${points[0]![1]}`;
    for (let i = 1; i < points.length; i++) {
      const [x0, y0] = points[i - 1]!;
      const [x1, y1] = points[i]!;
      const cx = (x0 + x1) / 2;
      d += ` C ${cx} ${y0}, ${cx} ${y1}, ${x1} ${y1}`;
    }

    const last = points[points.length - 1]!;
    const area = `${d} L ${last[0]} ${height} L 0 ${height} Z`;
    return { pathD: d, areaD: area, endX: last[0], endY: last[1] };
  }, [values, width, height]);

  if (!pathD) {
    return <div className={cn("h-8", className)} aria-hidden="true" />;
  }

  return (
    <svg
      className={cn("block", className)}
      width="100%"
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      role={ariaLabel ? "img" : "presentation"}
      aria-label={ariaLabel}
    >
      {fill && <path d={areaD} fill={fill} opacity={0.35} />}
      <path
        d={pathD}
        fill="none"
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
      {showEndDot && (
        <circle cx={endX} cy={endY} r={2.5} fill={stroke} stroke="white" strokeWidth={0.8} />
      )}
    </svg>
  );
}
